import React, { useState, useEffect } from 'react';
import { CalendarBlank, Fire, PenNib, VideoCamera, Package, Browser, Envelope, Trophy } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { growthAPI } from '@/utils/api';

const WeeklyReview = ({ onViewPlan }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeeklyReview();
  }, []);

  const fetchWeeklyReview = async () => {
    try {
      const response = await growthAPI.getWeeklyReview();
      setReview(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly review:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-white/10 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!review) return null;

  const totalActions = review.content_created + review.offers_created + review.sales_pages_created + review.email_campaigns_created;

  const stats = [
    { icon: PenNib, label: 'Content', value: review.content_created, color: 'text-purple-400' },
    { icon: Package, label: 'Offers', value: review.offers_created, color: 'text-amber-400' },
    { icon: Browser, label: 'Sales Pages', value: review.sales_pages_created, color: 'text-pink-400' },
    { icon: Envelope, label: 'Emails', value: review.email_campaigns_created, color: 'text-blue-400' },
    { icon: Fire, label: 'Streak', value: `${review.streak_days}d`, color: 'text-orange-400' },
  ];

  // Calculate performance message
  const getMessage = () => {
    if (totalActions >= 7) return { text: 'Amazing week! You\'re crushing it!', emoji: '🏆' };
    if (totalActions >= 5) return { text: 'Great progress this week!', emoji: '🔥' };
    if (totalActions >= 3) return { text: 'Good momentum building!', emoji: '💪' };
    if (totalActions >= 1) return { text: 'You\'re on your way!', emoji: '🚀' };
    return { text: 'Let\'s make this week count!', emoji: '⭐' };
  };

  const message = getMessage();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-6"
      data-testid="weekly-review"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <CalendarBlank size={20} className="text-indigo-400" weight="fill" />
          <h3 className="text-white font-semibold">Weekly Review</h3>
        </div>
        <span className="text-sm text-slate-400">
          {review.week_start} - {review.week_end}
        </span>
      </div>

      {/* Performance Message */}
      <div className="bg-white/5 rounded-lg p-3 mb-4 text-center">
        <span className="text-2xl mr-2">{message.emoji}</span>
        <span className="text-white font-medium">{message.text}</span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-2">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 rounded-lg p-3 text-center"
            >
              <Icon size={20} weight="fill" className={`${stat.color} mx-auto mb-1`} />
              <div className="text-white font-bold text-lg">{stat.value}</div>
              <div className="text-slate-400 text-xs">{stat.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Total Actions */}
      <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
        <span className="text-slate-400 text-sm">Total Actions This Week</span>
        <div className="flex items-center space-x-2">
          <Trophy size={18} weight="fill" className="text-yellow-400" />
          <span className="text-white font-bold text-lg">{totalActions}</span>
        </div>
      </div>

      {/* View Plan CTA */}
      {onViewPlan && (
        <button
          onClick={onViewPlan}
          className="mt-4 w-full py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 text-sm font-medium rounded-lg transition-colors"
        >
          View My Plan →
        </button>
      )}
    </motion.div>
  );
};

export default WeeklyReview;
